import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { CANADIAN_CITIES, EVENT_CATEGORIES } from '../lib/constants';
import { FIELD_INPUT, FIELD_LABEL, ImageUploader, SubmitButton, PremiumGate } from '../components/submitHelpers';

export default function SubmitEvent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    images: [],
    venue: '',
    address: '',
    city: '',
    date: '',
    time: '',
    end_time: '',
    price: '',
    organizer: '',
    eventbrite_url: '',
  });

  function update(k, v) { setForm({ ...form, [k]: v }); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.category) return toast.error('Select a category');
    if (!form.city) return toast.error('Select a city');
    if (!form.venue.trim()) return toast.error('Venue is required');
    if (!form.date) return toast.error('Date is required');
    if (!form.time) return toast.error('Start time is required');
    if (!form.organizer.trim()) return toast.error('Organizer is required');
    if (form.description.trim().length < 30) return toast.error('Description must be at least 30 characters');

    setLoading(true);
    try {
      await api('/submissions/event', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          title: form.title.trim(),
          description: form.description.trim(),
          venue: form.venue.trim(),
          address: form.address.trim() || form.venue.trim(),
          organizer: form.organizer.trim(),
          end_time: form.end_time || null,
          price: form.price.trim() || null,
          eventbrite_url: form.eventbrite_url.trim() || null,
        }),
      });
      toast.success('Submitted for review!');
      navigate('/app/my-submissions');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  if (!user?.is_premium) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <BackBar to="/app/submit" />
        <PremiumGate message="Submitting an event is a Premium-only feature." />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="submit-event-page">
      <BackBar to="/app/submit" />
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        Submit an Event
      </h1>
      <p className="text-sm text-[#6D6875] mb-6">We'll review and publish within 48 hours.</p>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="submit-event-form">
        <div>
          <label className={FIELD_LABEL}>Event title *</label>
          <input data-testid="event-title-input" className={FIELD_INPUT} value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="e.g. Afrobeats Night Toronto" />
        </div>

        <div>
          <label className={FIELD_LABEL}>Category *</label>
          <select data-testid="event-category-select" className={FIELD_INPUT} value={form.category} onChange={(e) => update('category', e.target.value)}>
            <option value="">Choose...</option>
            {EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className={FIELD_LABEL}>Description * <span className="text-xs font-normal text-[#B5B5B5]">(30+ characters)</span></label>
          <textarea data-testid="event-description-input" rows={4} maxLength={2000} className={FIELD_INPUT} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="What's the vibe? Who should come? What can guests expect?" />
          <div className="text-xs text-right text-[#B5B5B5] mt-1">{form.description.length}/2000</div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={FIELD_LABEL}>Venue *</label>
            <input data-testid="event-venue-input" className={FIELD_INPUT} value={form.venue} onChange={(e) => update('venue', e.target.value)} placeholder="Rebel Nightclub" />
          </div>
          <div>
            <label className={FIELD_LABEL}>City *</label>
            <select data-testid="event-city-select" className={FIELD_INPUT} value={form.city} onChange={(e) => update('city', e.target.value)}>
              <option value="">Choose...</option>
              {CANADIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={FIELD_LABEL}>Address (optional)</label>
          <input data-testid="event-address-input" className={FIELD_INPUT} value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="11 Polson St, Toronto, ON" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={FIELD_LABEL}>Date *</label>
            <input data-testid="event-date-input" type="date" className={FIELD_INPUT} value={form.date} onChange={(e) => update('date', e.target.value)} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Start time *</label>
            <input data-testid="event-time-input" type="time" className={FIELD_INPUT} value={form.time} onChange={(e) => update('time', e.target.value)} />
          </div>
          <div>
            <label className={FIELD_LABEL}>End time</label>
            <input data-testid="event-end-time-input" type="time" className={FIELD_INPUT} value={form.end_time} onChange={(e) => update('end_time', e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={FIELD_LABEL}>Organizer *</label>
            <input data-testid="event-organizer-input" className={FIELD_INPUT} value={form.organizer} onChange={(e) => update('organizer', e.target.value)} placeholder="Your name or company" />
          </div>
          <div>
            <label className={FIELD_LABEL}>Price (optional)</label>
            <input data-testid="event-price-input" className={FIELD_INPUT} value={form.price} onChange={(e) => update('price', e.target.value)} placeholder="$25 or Free" />
          </div>
        </div>

        <div>
          <label className={FIELD_LABEL}>Tickets URL (optional)</label>
          <input data-testid="event-eventbrite-input" type="url" className={FIELD_INPUT} value={form.eventbrite_url} onChange={(e) => update('eventbrite_url', e.target.value)} placeholder="https://eventbrite.com/..." />
        </div>

        <ImageUploader images={form.images} setImages={(images) => update('images', images)} max={3} testIdPrefix="event-submission" />

        <SubmitButton loading={loading} testId="event-submit-button">
          <Send className="w-5 h-5" />
          Submit for Review
        </SubmitButton>
      </form>
    </div>
  );
}

function BackBar({ to }) {
  return (
    <Link to={to} data-testid="submit-back-button" className="inline-flex items-center gap-2 text-sm font-semibold text-[#6D6875] hover:text-[#C8553D] mb-4">
      <ArrowLeft className="w-4 h-4" />
      Back
    </Link>
  );
}
